import { MAX_BID_MIN_VALUE_INT } from '@app/configurations';
import { GroupByValues } from '@app/core/models/api.models';
import { Goal, PerformanceGroup, UserMaxBid, UserMaxBidAPIResponse, Warning } from '@app/core/models/common.models';
import { Currency } from '@app/core/models/customer.models';
import {
  AllMetricsArray,
  CLICK_THROUGH_RATE,
  CLICK_THROUGH_RATE_CLICKS,
  CLICKS,
  CONVERSION_RATE,
  CONVERSIONS,
  CONVERSIONS_CLICKS,
  COST,
  COST_DAY,
  COST_FOR_CLICK,
  COST_FOR_PROFIT,
  COST_FOR_REVENUE,
  COST_FOR_VCPM,
  COST_PER_CLICK,
  COST_PER_CONVERSION,
  COST_PER_CONVERSION_CLICKS,
  COST_PER_MILLE,
  COST_REVENUE_RATIO,
  COST_REVENUE_RATIO_CLICKS,
  IMPRESSIONS,
  IMPRESSIONS_TOTAL,
  PG_GOAL_METRIC_COST_PER_CLICK,
  PG_GOAL_METRIC_COST_PER_CONVERSION,
  PG_GOAL_METRIC_COST_PER_CONVERSION_CLICKS,
  PG_GOAL_METRIC_COST_PER_DAY,
  PG_GOAL_METRIC_CRR,
  PG_GOAL_METRIC_CRR_CLICKS,
  PG_GOAL_METRIC_PROFIT_MAXIMIZATION,
  PG_GOAL_METRIC_ROAS,
  PG_GOAL_METRIC_ROAS_CLICKS,
  PG_GOAL_METRIC_ROI,
  PRICE,
  PROFIT,
  PROFIT_GROSS,
  PROFIT_NET,
  RETURN_ON_INVESTMENT,
  REVENUE,
  REVENUE_CLICKS,
  ROAS,
  ROAS_CLICKS,
  VCPMGoalMetricMaps,
  VIEW_IMPRESSIONS,
} from '@app/core/models/metrics.constants';
import {
  convertMicrostoNumbers,
  getACoS,
  getClickThroughRate,
  getConversionRate,
  getCostPerConversion,
  getEffectiveCPC,
  getEffectiveCPM,
  getReturnOnAdSpend,
  getReturnOnInvestment,
  isMonetaryValue,
  summationByMetric,
} from '@app/core/services/metrics-calculations.service';
import { getUniqueItemsBy } from '@app/core/services/utilities.service';
import { getDateRangeLast30Days } from '@app/core/utilities/date.utilities';
import { microsToNumber, numberToMicros } from '@app/core/utilities/number.utilities';
import { GetMetricTypeInfoArray } from '@app/core/utilities/refiners.utilities';
import { snakeToCamelCase } from '@app/core/utilities/string.utilities';
import { differenceInDays, format } from 'date-fns';
import { GoalsSummaryModel, ViewableMetricsForOptimizationTypes } from '../pg-goals.models';
import { PGListItem, PriorityOrder, WarningFormatted, WarningResolveState } from '../pg-list/pg-list.models';

export const IsMaxBidValid = (value: number | string): boolean => {
  if (!value) return false;
  if (isNaN(<number>value)) return false;
  if (parseFloat(<string>value) < MAX_BID_MIN_VALUE_INT) return false;

  return true;
};

export function MaxBidValueForPayload(dt: UserMaxBid, currency?: Currency): UserMaxBidAPIResponse {
  if (!currency) currency = dt.currency;
  if (dt?.value === 0) throw new Error('max bid Value For Payload cannot be zero');

  return { micros: numberToMicros(dt?.value || MAX_BID_MIN_VALUE_INT), currency };
}

export function MaxBidValueForUI(
  dt: UserMaxBidAPIResponse | UserMaxBid,
  currency?: Currency,
  valueInMicros: boolean = false,
  returnNullValue = true,
): UserMaxBid {
  if (!currency) currency = dt.currency;
  if (dt == null) {
    if (returnNullValue) return { value: null, currency };
    return null;
  }

  let value;
  if (valueInMicros)
    value = (<UserMaxBidAPIResponse>dt)?.micros == null ? null : microsToNumber((<UserMaxBidAPIResponse>dt)?.micros);
  else value = (<UserMaxBid>dt)?.value == null ? null : (<UserMaxBid>dt)?.value;

  return { value, currency };
}

export const GetWarningResolveState = (type) => {
  if (['CAMPAIGN-OUT-OF-BUDGET'].includes(type)) return WarningResolveState.NO_RESOLVE;
  else if (
    [
      'CAMPAIGN-PAUSED',
      'CAMPAIGN-ACTIVATED',
      'CAMPAIGN-INVALID-OPTIMIZATION-TYPE',
      'LEGACY-INVALID-CAMPAIGN-OPTIMIZATION-TYPE',
    ].includes(type)
  )
    return WarningResolveState.CAMPAIGNS_CHANGE;
  else return WarningResolveState.GOAL_CHANGE;
};

export const FormattedWarnings = (warnings: Warning[], translations) => {
  const duplicatesRemoved = getUniqueItemsBy(warnings, 'type');
  const sorted = duplicatesRemoved.sort((a, b) => PriorityOrder[b.priority] - PriorityOrder[a.priority]);
  const priority = sorted.find((d) => !d.acknowledgedAt)?.priority || '';

  const warningsFormatted = sorted
    .filter((d) => !!translations[d.type])
    .map((d: WarningFormatted) => ({
      ...d,
      resolveState: GetWarningResolveState(d.type),
      campaigns: warnings.filter((e) => e.type === d.type && !!e.campaignId).map((e) => e.adspertCampaignId),
    }));
  return { showWarningsBanner: !!warningsFormatted.length, warningsFormatted, warningsPriority: priority };
};

export const MetricsContextForGoalsOptiTypes = {
  CLICK: GetMetricTypeInfoArray([COST, IMPRESSIONS, CLICKS, CLICK_THROUGH_RATE, COST_PER_CLICK]),
  REVENUE: GetMetricTypeInfoArray([
    COST,
    CLICKS,
    CLICK_THROUGH_RATE,
    COST_PER_CLICK,
    CONVERSIONS,
    CONVERSION_RATE,
    COST_PER_CONVERSION,
    REVENUE,
    COST_REVENUE_RATIO,
    ROAS,
  ]),
  PROFIT: GetMetricTypeInfoArray([
    COST,
    CLICKS,
    CLICK_THROUGH_RATE,
    COST_PER_CLICK,
    CONVERSIONS,
    CONVERSION_RATE,
    COST_PER_CONVERSION,
    PROFIT_GROSS,
    PROFIT_NET,
    RETURN_ON_INVESTMENT,
  ]),
  VCPM: GetMetricTypeInfoArray([
    COST,
    CLICKS,
    CLICK_THROUGH_RATE,
    COST_PER_CLICK,
    CONVERSIONS,
    CONVERSION_RATE,
    COST_PER_CONVERSION,
    REVENUE,
    COST_REVENUE_RATIO,
    ROAS,
    VIEW_IMPRESSIONS,
    COST_PER_MILLE,
  ]),
};

export const GetCampaignsWithAllMetrics = (data) => {
  return convertMicrostoNumbers(data).map((item: any) => ({
    adspertId: item.adspertAccountId,
    accountName: item.accountName,
    platform: item.accountPlatform,
    accountTags: item.accountTagsUser || [],
    pgName: item.performanceGroupName,
    pgId: item.performanceGroupId,
    portfolioId: item.adspertPortfolioId,
    id: item.adspertCampaignId,
    campaignName: item.campaignName,
    campaignLabels: item.campaignLabels,
    campaignStatus: item.campaignStatus,
    campaignOptimizationType: item.campaignOptimizationType,
    campaignType: item.campaignType ? item.campaignType?.replace(/-/g, ' ') : [],
    allMetrics: AllMetricsArray.filter((key) => item[snakeToCamelCase(key)] !== undefined).map((key) => ({
      key,
      value: item[snakeToCamelCase(key)],
    })),
  }));
};

export const GoalsSummaryData = (data, profitKey, days: number = 30): GoalsSummaryModel => {
  if (!data) return null;

  const summary = {
    [COST]: summationByMetric(data, COST),
    [COST_FOR_PROFIT]: summationByMetric(data, COST_FOR_PROFIT),
    [COST_FOR_CLICK]: summationByMetric(data, COST_FOR_CLICK),
    [COST_FOR_REVENUE]: summationByMetric(data, COST_FOR_REVENUE),
    [COST_FOR_VCPM]: summationByMetric(data, COST_FOR_VCPM),

    [IMPRESSIONS]: summationByMetric(data, IMPRESSIONS),
    [CLICKS]: summationByMetric(data, CLICKS),
    [CONVERSIONS]: summationByMetric(data, CONVERSIONS),
    [REVENUE]: summationByMetric(data, REVENUE),
    [PROFIT]: summationByMetric(data, profitKey),
    [PROFIT_GROSS]: summationByMetric(data, PROFIT_GROSS),
    [PRICE]: summationByMetric(data, PRICE),
    [CONVERSIONS_CLICKS]: summationByMetric(data, CONVERSIONS_CLICKS),
    [REVENUE_CLICKS]: summationByMetric(data, REVENUE_CLICKS),
    [IMPRESSIONS_TOTAL]: summationByMetric(data, IMPRESSIONS_TOTAL),
    [VIEW_IMPRESSIONS]: summationByMetric(data, VIEW_IMPRESSIONS),

    cost_optimization_type_vcpm: 0,
  };

  const COST_WITHOUT_CLICKS = summary[COST] - summary[COST_FOR_CLICK];

  summary[CONVERSION_RATE] = getConversionRate(summary[CONVERSIONS], summary[CLICKS]);
  summary[COST_PER_MILLE] = getEffectiveCPM(summary[COST_FOR_VCPM], summary[VIEW_IMPRESSIONS]);
  summary[RETURN_ON_INVESTMENT] = getReturnOnInvestment(summary[PROFIT_GROSS], summary[COST_FOR_PROFIT]);

  summary[COST_PER_CLICK] = getEffectiveCPC(summary[COST], summary[CLICKS]);

  summary[CLICK_THROUGH_RATE] = getClickThroughRate(summary[CLICKS], summary[IMPRESSIONS_TOTAL]);
  summary[CLICK_THROUGH_RATE_CLICKS] = getClickThroughRate(summary[CLICKS], summary[IMPRESSIONS]);

  summary[COST_PER_CONVERSION] = getCostPerConversion(COST_WITHOUT_CLICKS, summary[CONVERSIONS]);
  summary[COST_PER_CONVERSION_CLICKS] = getCostPerConversion(COST_WITHOUT_CLICKS, summary[CONVERSIONS_CLICKS]);

  summary[COST_REVENUE_RATIO] = getACoS(COST_WITHOUT_CLICKS, summary[REVENUE]);
  summary[COST_REVENUE_RATIO_CLICKS] = getACoS(COST_WITHOUT_CLICKS, summary[REVENUE_CLICKS]);

  summary[ROAS] = getReturnOnAdSpend(summary[REVENUE], summary[COST_FOR_REVENUE]);
  summary[ROAS_CLICKS] = getReturnOnAdSpend(summary[REVENUE_CLICKS], summary[COST_FOR_REVENUE]);

  return {
    [PG_GOAL_METRIC_COST_PER_DAY]: summary[COST] / days,
    [PG_GOAL_METRIC_COST_PER_CLICK]: summary[COST_PER_CLICK],
    [PG_GOAL_METRIC_COST_PER_CONVERSION]: summary[COST_PER_CONVERSION],
    [PG_GOAL_METRIC_CRR]: summary[COST_REVENUE_RATIO],
    [PG_GOAL_METRIC_ROI]: summary[RETURN_ON_INVESTMENT],
    [PG_GOAL_METRIC_ROAS]: summary[ROAS],
    [PG_GOAL_METRIC_PROFIT_MAXIMIZATION]: summary[PROFIT],
    [PG_GOAL_METRIC_ROAS_CLICKS]: summary[ROAS_CLICKS],
    [PG_GOAL_METRIC_CRR_CLICKS]: summary[COST_REVENUE_RATIO_CLICKS],
    [PG_GOAL_METRIC_COST_PER_CONVERSION_CLICKS]: summary[COST_PER_CONVERSION_CLICKS],
  };
};

export function getFilterValuesForPGEndpoint(filterValues) {
  const returnValue = { ...filterValues };
  const mapper = {
    performanceGroupEnabled: 'enabled',
    performanceGroupOptimizationType: 'optimizationType',
    performanceGroupGoalTypes: 'goalTypes',
    campaignAmzRasCampaign: 'campaignAmzRasCampaign',
  };
  Object.keys(returnValue).forEach((key) => {
    if (mapper[key]) {
      returnValue[mapper[key]] = returnValue[key];
      delete returnValue[key];
    }
  });

  return returnValue;
}
export function mapMetricsForOpAndGoal(d: PGListItem) {
  return (
    ViewableMetricsForOptimizationTypes[d.optimizationType].goals[d.goals[0]?.goalType] ??
    ViewableMetricsForOptimizationTypes[d.optimizationType].default
  ).map((t) => (t === COST_DAY ? COST : t));
}

export function adjustMonetaryValues(goal) {
  if (isMonetaryValue(goal.goalType) && !isNaN(goal.goalValue)) goal.goalValue = microsToNumber(goal.goalValue);
  return goal;
}
export function getGoalsWithUserCurrency(d: PGListItem, userCurrency: Currency, rates: Record<Currency, number>) {
  const factor = rates[`${userCurrency}-${d.pgCurrency}`];
  return d.goals.map((d) => {
    const goal = { ...d };
    if (isMonetaryValue(goal.goalType) && !isNaN(goal.goalValue)) {
      goal.goalValue = goal.goalValue / factor;
      goal.goalMicros = goal.goalMicros / factor;
      goal.goalCurrency = userCurrency;
    }
    return goal;
  });
}
export function getMaxBidWithUserCurrency(d: PGListItem, userCurrency: Currency, rates: Record<Currency, number>) {
  if (!d.userMaxBid) return d.userMaxBid;

  const factor = rates[`${userCurrency}-${d.pgCurrency}`];
  const maxbid = { ...d.userMaxBid };
  if (!!maxbid?.currency) {
    maxbid.value = maxbid.value / factor;
    maxbid.currency = userCurrency;
  }
  return maxbid;
}

export function GetDatesRequestInfo(dates?: any) {
  const [filter_date_ge, filter_date_le] = dates
    ? dates
    : getDateRangeLast30Days(false).map((d) => format(d, 'yyyy-MM-dd'));
  const limit = differenceInDays(new Date(filter_date_le), new Date(filter_date_ge)) + 1 || 30;

  return { limit, group_by: GroupByValues.DATE, filter_date_ge, filter_date_le };
}

export function ExlcudeFewWarningsForEmptyPG(data: PerformanceGroup) {
  if (data.campaigns.length) return data.warnings;
  return data.warnings.filter(
    ({ type }) => !['CAMPAIGN-DELETED', 'ACCOUNT-MOVED', 'ACCOUNT-DISCONNECTED', 'ACCOUNT-ARCHIVED'].includes(type),
  );
}

export function FormatPGListMetrics(pg) {
  if (!pg) return {};
  const tmp = convertMicrostoNumbers(pg);
  return AllMetricsArray.filter((key) => tmp[snakeToCamelCase(key)] !== undefined).map((key) => ({
    key,
    value: tmp[snakeToCamelCase(key)],
  }));
}

export function getGoalsCurrentData(goals: Goal[], summary: GoalsSummaryModel, currency?: Currency) {
  const currentData = [];
  const vcpmCurrentData = {};

  goals.forEach(({ goalType, goalCurrency }) => {
    currentData.push({ value: summary[goalType], type: goalType, currency: currency || goalCurrency });
    const vcpmGoal = VCPMGoalMetricMaps[goalType];
    if (!!vcpmGoal)
      vcpmCurrentData[goalType] = { value: summary[vcpmGoal], type: vcpmGoal, currency: currency || goalCurrency };
  });
  return { currentData, vcpmCurrentData };
}

export const CustomerPerformanceFields = [
  'date',
  'adspertAccountId',
  'accountId',
  'accountName',
  'accountPlatform',
  'accountOptimize',
  'accountTagsUser',
  'accountIsDemo',
  'performanceGroupId',
  'performanceGroupName',
  'performanceGroupEnabled',
  'performanceGroupOptimizationType',
  'performanceGroupGoals',
  'performanceGroupGoalTypes',
  'performanceGroupPlatforms',
  'performanceGroupUserMaxBid',
  'adspertPortfolioId',
  'amzPortfolioId',
  'amzPortfolioName',
  'adspertCampaignId',
  'campaignId',
  'campaignName',
  'campaignType',
  'campaignEbyMarketplaceId',
  'campaignStatus',
  'campaignOptimize',
  'campaignOptimizationType',
  'campaignOptimizationSupport',
  'campaignLabels',
  'campaignUserMaxBid',
  'impressions',
  'viewImpressions',
  'impressionsTotal',
  'clicks',
  'conversions',
  'conversionsClicks',
  'conversionsAdjusted',
  'conversionsForRevenue',
  'conversionsForRevenueAdjusted',
  'profitGross',
  'profitGrossAdjusted',
  'profitNet',
  'profitNetAdjusted',
  'revenue',
  'revenueClicks',
  'revenueAdjusted',
  'revenueTotal',
  'revenueTotalRollingAvg',
  'revenueOrganic',
  'revenueOrganicAdjusted',
  'budgetAmount',
  'cost',
  'costForProfit',
  'costForRevenue',
  'costForClick',
  'costForVcpm',
  'costForOrganic',
  'costForOrganicRollingAvg',
  'costPerClick',
  'costPerMViewImpressions',
  'costPerConversion',
  'costPerConversionClicks',
  'costPerConversionAdjusted',
  'conversionRate',
  'conversionRateClicks',
  'conversionRateAdjusted',
  'clickThroughRate',
  'clickThroughRateClicks',
  'returnOnInvestment',
  'returnOnInvestmentAdjusted',
  'returnOnAdSpend',
  'returnOnAdSpendClicks',
  'returnOnAdSpendAdjusted',
  'costRevenueRatio',
  'costRevenueRatioClicks',
  'costRevenueRatioAdjusted',
  'costRevenueTotalRatio',
  'costRevenueTotalRatioRollingAvg',
  'revenuePerConversion',
  'revenuePerConversionAdjusted',
  'budgetCoverage',
  'hasVcpm',
];
